import React, { useState } from 'react';
import '../css/WorldTour.css';

const WorldTour = ({ goalName, onGoalComplete, onBackToGoals }) => {
  const [step, setStep] = useState(1); // 1 = input form, 2 = results
  const [formData, setFormData] = useState({
    frequency: 1,
    startDate: new Date(new Date().getFullYear() + 1, 0, 1).toISOString().split('T')[0],
    type: 'abroad',
    numPeople: 2,
    costPerPerson: 100000,
    inflationRate: 5,
    numVacations: 5,
    expectedROR: 12
  });

  // Results state
  const [vacationPlan, setVacationPlan] = useState([]);
  const [totalSIP, setTotalSIP] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('Rate') || name.includes('ROR') || 
              name === 'frequency' || name === 'costPerPerson' ? 
              parseFloat(value) : 
              (name === 'numPeople' || name === 'numVacations' ? 
               parseInt(value) : 
               value)
    }));
  };

  const calculateVacationPlan = () => {
    const {
      frequency,
      startDate,
      numPeople,
      costPerPerson,
      inflationRate,
      numVacations,
      expectedROR
    } = formData;

    const today = new Date();
    const firstVacationDate = new Date(startDate);
    
    // Calculate months to first vacation
    let monthsToFirstVacation = 
      (firstVacationDate.getFullYear() - today.getFullYear()) * 12 + 
      (firstVacationDate.getMonth() - today.getMonth());
    
    if (monthsToFirstVacation < 0) {
      monthsToFirstVacation = 0;
    }
    
    const monthlyRate = expectedROR / 100 / 12;
    const inflationDecimal = inflationRate / 100;
    
    const plan = [];
    let totalSIP = 0;
    
    for (let i = 0; i < numVacations; i++) {
      // Calculate vacation date
      const vacationDate = new Date(firstVacationDate);
      vacationDate.setFullYear(firstVacationDate.getFullYear() + i * frequency);
      
      // Calculate cost
      let cost = numPeople * costPerPerson;
      if (i === 0) {
        // First vacation
        cost *= Math.pow(1 + inflationDecimal, monthsToFirstVacation / 12);
      } else {
        // Subsequent vacations
        cost = plan[i - 1].cost * Math.pow(1 + inflationDecimal, frequency);
      }
      
      // Calculate SIP required
      let sipRequired = 0;
      if (i === 0 && monthsToFirstVacation > 0) {
        // First vacation SIP
        sipRequired = cost * monthlyRate / 
                     (Math.pow(1 + monthlyRate, monthsToFirstVacation) - 1);
      } else if (i > 0) {
        // Subsequent vacations SIP
        const months = frequency * 12;
        sipRequired = cost * monthlyRate / 
                     (Math.pow(1 + monthlyRate, months) - 1);
      }
      
      totalSIP += sipRequired;
      
      plan.push({
        date: vacationDate,
        cost: Math.round(cost / 100) * 100, // Round to nearest 100
        sipRequired: Math.round(sipRequired / 100) * 100 // Round to nearest 100
      });
    }
    
    setVacationPlan(plan);
    setTotalSIP(Math.round(totalSIP / 100) * 100);
    setStep(2); // Move to results step
  };

  const handleSubmit = () => {
    // Return consistent DTO structure
    onGoalComplete({
      selectedValue: formData.selectedValue,  // Keep selected dropdown value
      frequency: formData.frequency,
      startDate: formData.startDate,
      type: formData.type,
      numPeople: formData.numPeople,
      costPerPerson: formData.costPerPerson,
      inflationRate: formData.inflationRate,
      numVacations: formData.numVacations,
      expectedROR: formData.expectedROR,
      vacationPlan,
      totalSIP
    });
  };

  const handleRecalculate = () => {
    setStep(1); // Go back to input form
  };

  // Format currency in Indian style
  const formatCurrency = (amount) => {
    return '₹' + amount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Format date as "dd-mmm-yy"
  const formatDate = (date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  // Step 1: Input Form
  const renderInputForm = () => (
    <div className="world-tour-container">
      <h3>World Tour Planning</h3>
      
      <div className="form-section">
        <div className="form-group">
          <label>Frequency:</label>
          <select 
            name="frequency" 
            value={formData.frequency} 
            onChange={handleChange}
          >
            <option value="1">Every 1 Year</option>
            <option value="2">Every 2 Years</option>
            <option value="3">Every 3 Years</option>
          </select>
        </div>
        
        <div className="form-group">
          <label>Start Date:</label>
          <input 
            type="date" 
            name="startDate" 
            value={formData.startDate} 
            onChange={handleChange} 
          />
        </div>
        
        <div className="form-group">
          <label>Type:</label>
          <div className="radio-group">
            <label>
              <input 
                type="radio" 
                name="type" 
                value="abroad" 
                checked={formData.type === 'abroad'} 
                onChange={handleChange} 
              />
              Abroad
            </label>
            <label>
              <input 
                type="radio" 
                name="type" 
                value="domestic" 
                checked={formData.type === 'domestic'} 
                onChange={handleChange} 
              />
              Domestic
            </label>
          </div>
        </div>
        
        <div className="form-group">
          <label>Number of People:</label>
          <input 
            type="number" 
            name="numPeople" 
            min="1"
            value={formData.numPeople} 
            onChange={handleChange} 
          />
        </div>
        
        <div className="form-group">
          <label>Cost per Person (₹):</label>
          <input 
            type="number" 
            name="costPerPerson" 
            min="0"
            value={formData.costPerPerson} 
            onChange={handleChange} 
          />
        </div>
        
        <div className="form-group">
          <label>Inflation Rate (%):</label>
          <input 
            type="number" 
            name="inflationRate" 
            min="0" 
            step="0.1"
            value={formData.inflationRate} 
            onChange={handleChange} 
          />
        </div>
        
        <div className="form-group">
          <label>Number of Vacations:</label>
          <input 
            type="number" 
            name="numVacations" 
            min="1"
            value={formData.numVacations} 
            onChange={handleChange} 
          />
        </div>
        
        <div className="form-group">
          <label>Expected Return (%):</label>
          <input 
            type="number" 
            name="expectedROR" 
            min="0" 
            step="0.1"
            value={formData.expectedROR} 
            onChange={handleChange} 
          />
        </div>
      </div>
      
      <div className="action-buttons">
        <button className="back-button" onClick={onBackToGoals}>
          ← Back to Goals
        </button>
        <button className="calculate-button" onClick={calculateVacationPlan}>
          Calculate
        </button>
      </div>
    </div>
  );

  // Step 2: Results
  const renderResults = () => (
    <div className="world-tour-container">
      <h3>World Tour Planning</h3>
      
      <div className="summary-section">
        <h4>Vacation Plan Summary</h4>
        
        <div className="summary-details">
          <div className="detail-row">
            <span className="detail-label">Frequency:</span>
            <span className="detail-value">Every {formData.frequency} Year(s)</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Type:</span>
            <span className="detail-value">{formData.type === 'abroad' ? 'Abroad' : 'Domestic'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">People:</span>
            <span className="detail-value">{formData.numPeople}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Cost per Person:</span>
            <span className="detail-value">{formatCurrency(formData.costPerPerson)}</span>
          </div>
        </div>
        
        <div className="results-section">
          <h4>Vacation Plan</h4>
          <table className="vacation-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Estimated Cost</th>
                <th>SIP Required</th>
              </tr>
            </thead>
            <tbody>
              {vacationPlan.map((vacation, index) => (
                <tr key={index}>
                  <td>{formatDate(vacation.date)}</td>
                  <td>{formatCurrency(vacation.cost)}</td>
                  <td>{formatCurrency(vacation.sipRequired)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          

        </div>
      </div>
      
      <div className="action-buttons">
        <button className="back-button" onClick={handleRecalculate}>
          ← Recalculate
        </button>
        <button className="submit-button" onClick={handleSubmit}>
          Save & Continue
        </button>
      </div>
    </div>
  );

  return step === 1 ? renderInputForm() : renderResults();
};

export default WorldTour;